/**
 * Simple event manager
 * @class
 *
 * (Implemented as an old-style constructor function instead of a class
 * so it can be used as a mixin too)
 */
export default function EventManager() {
  this._listening = {};
}

/**
 * Attaches the callback that will be invoked whenever the event is fired
 * with the arguments passed to `trigger`.
 * The callback will be invoked once per event even if it was added multiple times.
 * The callback will also be invoked when any event in that namespace is fired.
 * @param {String} event - event name
 * @param {Function} callback - handler function
 * @returns {Function} - function that detaches the handler
 * @example
 * // `handler` will be called when `loaded`, `loaded:wid-1` etc. occur
 * const unsubscribe = eventManager.on('loaded', handler);
 * unsubscribe();
 */
EventManager.prototype.on = function(event, callback) {
  if (!(event in this._listening)) {
    this._listening[event] = new Set();
  }
  this._listening[event].add(callback);
  return () => this.off(event, callback);
};

/**
 * Attaches the callback that will be invoked when the event is fired and then removed
 * @param {String} event - event name
 * @param {Function} callback - handler function
 * @returns {Function} - function that detaches the handler
 */
EventManager.prototype.once = function(event, callback) {
  const self = this;
  function once() {
    self.off(event, once); // should be called before the callback in case it throws an error
    callback(...arguments);
  }
  return this.on(event, once);
};

/**
 * Removes a previously attached callback for the event
 * @param {String} event - event name
 * @param {Function} callback - handler function
 */
EventManager.prototype.off = function(event, callback) {
  if (!(event in this._listening)) { return; }

  this._listening[event].delete(callback);
  if (this._listening[event].size === 0) {
    delete this._listening[event];
  }
};

/**
 * Invokes callbacks for the given event in a synchronous manner.
 * The arguments will be passed along to the event callbacks.
 * Also invokes the callbacks for events in the outer namespaces.
 * @param {String} event - event name
 * @param {...*} arg - arguments to be passed to the handlers
 * @example
 * // will call handlers for `loaded:wid-1` and `loaded`, but not `loaded:wid-2`
 * eventManager.trigger('loaded:wid-1', data);
 */
EventManager.prototype.trigger = function(event, ...args) {
  if (event in this._listening) {
    this._listening[event].forEach((callback) => {
      // make sure next callbacks will be called even if this one throws an error
      try {
        callback(...args);
      } catch (error) {
        console.error(error);
      }
    });
  }

  // discard the rightmost part after the last colon
  const namespace = event.replace(/:[^:]*$/, '');
  if (namespace !== event) {
    this.trigger(namespace, ...args);
  }
};

EventManager.prototype.fire = EventManager.prototype.trigger;

/**
 * Removes all listeners
 */
EventManager.prototype.destroy = function() {
  delete this._listening;
};

/**
 * Extends the object with custom events functionality
 * @param {Object} target - object to extend
 */
EventManager.mixInto = function mixinEvents(target) {
  Object.assign(target, EventManager.prototype);
  EventManager.call(target);
};
